import { useQuery } from '@tanstack/react-query';
import { Contact, CrmContactsService, ChannelType } from 'src/api/generated';
import { standardizePhoneNumber } from 'src/modules/shared/utils/formatting/phone.util';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { useContactAddresses } from './contact-addresses';

const MAX_CONTACTS_TO_FETCH = 5;

interface UseContactsByPhoneParams {
  phoneNumber: string | null;
  enabled?: boolean;
}

interface UseContactsByPhoneResponse {
  contacts: Contact[];
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to find contacts by phone number
 * First searches for addresses with the standardized phone number, then fetches the associated contacts
 */
export const useContactsByPhone = ({
  phoneNumber,
  enabled = true,
}: UseContactsByPhoneParams): UseContactsByPhoneResponse => {
  const standardizedPhone = phoneNumber
    ? standardizePhoneNumber(phoneNumber)
    : null;

  const addressQuery = createQuery()
    .equals('address', standardizedPhone || '')
    .equals('channelType', ChannelType.PHONE)
    .take(20)
    .build();

  const {
    addresses,
    loading: addressesLoading,
    error: addressesError,
  } = useContactAddresses(addressQuery, enabled && !!standardizedPhone);

  const contactIds = [
    ...new Set(addresses.map((addr) => addr.contactId).filter(Boolean)),
  ];

  const fetchContacts = async (): Promise<Contact[]> => {
    if (contactIds.length === 0) return [];

    const contactIdsToFetch = contactIds.slice(0, MAX_CONTACTS_TO_FETCH);

    const contactPromises = contactIdsToFetch.map((id) =>
      CrmContactsService.getContact(id!)
    );

    const results = await Promise.allSettled(contactPromises);

    return results
      .filter(
        (result): result is PromiseFulfilledResult<Contact | null> =>
          result.status === 'fulfilled' && result.value !== null
      )
      .map((result) => result.value!);
  };

  const {
    data: contacts = [],
    error: contactsError,
    isLoading: contactsLoading,
  } = useQuery({
    queryKey: ['crm/contacts/by-phone', standardizedPhone, contactIds],
    queryFn: fetchContacts,
    enabled:
      enabled &&
      !!standardizedPhone &&
      !addressesLoading &&
      contactIds.length > 0,
  });

  return {
    contacts,
    loading: addressesLoading || contactsLoading,
    error: addressesError || contactsError,
  };
};
